/* Reuse video-media for lazy loading and background playback. Native controls
   take over only after an intentional user gesture; sources are never replaced. */
(() => {
  if (customElements.get('editorial-video')) return;
  customElements.define('editorial-video', class extends HTMLElement {
    connectedCallback() {
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = window.matchMedia('(max-width: 767px)');
      this.backgroundStates = new WeakMap();
      this.watchButton = this.querySelector('[data-watch-video]');
      this.exitButton = this.querySelector('[data-exit-video]');
      this.onWatch = () => this.enterPlayer();
      this.onExit = () => this.exitPlayer();
      this.onKey = event => {
        if (event.key === 'Escape' && this.activeMedia) {
          event.preventDefault();
          this.exitPlayer();
        }
      };
      this.onViewport = () => {
        if (this.activeMedia && this.activeMedia !== this.visibleMedia) {
          this.exitPlayer(false, false);
        }
        this.refreshWatchButton();
      };
      this.onMotion = () => {
        if (this.motion.matches) {
          this.querySelectorAll('video-media').forEach(player => {
            if (player !== this.activeMedia) {
              player.paused = true;
              player.pause();
            }
          });
        }
      };
      this.onMotion();
      this.refreshWatchButton();
      this.motion.addEventListener('change', this.onMotion);
      this.mobile.addEventListener('change', this.onViewport);
      this.watchButton?.addEventListener('click', this.onWatch);
      this.exitButton?.addEventListener('click', this.onExit);
      this.addEventListener('keydown', this.onKey);
    }
    get visibleMedia() {
      return [...this.querySelectorAll('video-media')].find(media => getComputedStyle(media).display !== 'none');
    }
    refreshWatchButton() {
      const media = this.visibleMedia;
      if (this.watchButton) {
        // External embeds retain their existing behavior; native controls are
        // offered only for the selected Shopify-hosted video.
        this.watchButton.hidden = !!this.activeMedia || !media || media.hasAttribute('host') || !this.watchButton.textContent.trim();
      }
    }
    setPlayerMode(enabled) {
      this.toggleAttribute('data-player-mode', enabled);
      this.querySelectorAll('.editorial-video__content, .deferred-poster').forEach(element => {
        element.toggleAttribute('inert', enabled);
        if (enabled) element.setAttribute('aria-hidden', 'true');
        else element.removeAttribute('aria-hidden');
      });
      this.watchButton?.setAttribute('aria-pressed', String(enabled));
      if (this.exitButton) this.exitButton.hidden = !enabled;
      this.refreshWatchButton();
    }
    enterPlayer() {
      const media = this.visibleMedia;
      if (!media || media.hasAttribute('host') || this.activeMedia || !this.exitButton) return;
      const preview = media.querySelector('video') || media.querySelector('template')?.content.querySelector('video');
      if (!preview) return;
      if (!this.backgroundStates.has(media)) {
        this.backgroundStates.set(media, {
          autoplay: media.hasAttribute('autoplay'),
          loop: preview.loop,
          tabindex: preview.getAttribute('tabindex')
        });
      }
      // Suppress automatic resume while native controls own play/pause.
      media.removeAttribute('autoplay');
      media.paused = true;
      // The existing getter synchronously materializes the native template once.
      // Keep audio activation and play() inside this original click handler.
      void media.player;
      const video = media.querySelector('video');
      if (!video) return;
      this.activeMedia = media;
      this.activeVideo = video;
      video.autoplay = false;
      video.muted = false;
      video.loop = false;
      video.controls = true;
      video.playsInline = true;
      video.setAttribute('tabindex', '0');
      this.setPlayerMode(true);
      this.clearStatus();
      this.tryPlay(video);
      video.focus({ preventScroll: true });
    }
    exitPlayer(resume = true, restoreFocus = true) {
      const media = this.activeMedia, video = this.activeVideo;
      if (!media || !video) return;
      const state = this.backgroundStates.get(media);
      // Mute before restoring any automatic background behavior.
      video.muted = true;
      video.controls = false;
      video.loop = state.loop;
      video.autoplay = state.autoplay && !this.motion.matches;
      media.toggleAttribute('autoplay', state.autoplay);
      media.paused = this.motion.matches || !resume;
      if (state.tabindex === null) video.removeAttribute('tabindex');
      else video.setAttribute('tabindex', state.tabindex);
      this.activeMedia = this.activeVideo = null;
      this.setPlayerMode(false);
      this.clearStatus();
      if (resume && !this.motion.matches) this.tryPlay(video);
      else video.pause();
      if (restoreFocus && !this.watchButton?.hidden) this.watchButton?.focus({ preventScroll: true });
    }
    clearStatus() {
      const status = this.querySelector('[data-player-status]');
      if (status) status.textContent = '';
    }
    tryPlay(video) {
      video.play()?.catch(() => {
        if (!this.isConnected) return;
        const status = this.querySelector('[data-player-status]');
        if (status) status.textContent = 'Playback could not start. Use the play control to try again.';
      });
    }
    disconnectedCallback() {
      this.exitPlayer(false, false);
      this.motion?.removeEventListener('change', this.onMotion);
      this.mobile?.removeEventListener('change', this.onViewport);
      this.watchButton?.removeEventListener('click', this.onWatch);
      this.exitButton?.removeEventListener('click', this.onExit);
      this.removeEventListener('keydown', this.onKey);
      this.querySelectorAll('video-media').forEach(player => player.pause());
    }
  });
})();
